import { useEffect, useRef, useState } from "react";
import { Sprout } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { fruits } from "@/data/fruits";

const lineColors = {
  love: "#1A3626",
  joy: "#C87961",
  peace: "#9EB3C2",
  patience: "#7D8B64",
  kindness: "#D6A96A",
  goodness: "#5F7F75",
  faithfulness: "#A85F4B",
  gentleness: "#73889A",
  self_control: "#B98B79",
};

const chartMargin = { top: 12, right: 20, left: -10, bottom: 0 };
const yAxisDomain = [1, 5];
const tooltipStyle = { borderRadius: 16, border: "1px solid #E5E1D8" };
const legendStyle = { color: "#4A5D4E", fontSize: 12 };
const dotStyle = { r: 3 };
const activeDotStyle = { r: 5 };
const chartHeight = 360;
const minChartWidth = 320;

const readChartWidth = (element) => {
  const measuredWidth = Math.floor(element.getBoundingClientRect().width);
  return measuredWidth > 0 ? Math.max(measuredWidth, minChartWidth) : 0;
};

const useChartSize = () => {
  const wrapperRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return undefined;

    const updateWidth = () => setWidth(readChartWidth(element));

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ready: width > 0, width, wrapperRef };
};

export const DashboardChart = ({ chartData }) => {
  const { ready, width, wrapperRef } = useChartSize();

  return (
    <section className="rounded-2xl border border-[#E5E1D8] bg-white p-5 shadow-[0_8px_30px_rgba(26,54,38,0.04)] sm:p-8" data-testid="dashboard-chart-section">
      <div className="mb-6 flex items-center gap-3" data-testid="dashboard-chart-header">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F9F6F0] text-[#1A3626]" data-testid="dashboard-chart-icon"><Sprout size={18} /></span>
        <div>
          <h2 className="font-serif-display text-3xl font-semibold text-[#1A3626]" data-testid="dashboard-chart-title">Trend over time</h2>
          <p className="text-sm text-[#8C9B90]" data-testid="dashboard-chart-subtitle">Last 30 reflections shown</p>
        </div>
      </div>
      <div ref={wrapperRef} className="h-[360px] w-full overflow-hidden" data-testid="dashboard-chart-wrapper">
        {chartData.length && ready ? (
          <LineChart data={chartData} height={chartHeight} margin={chartMargin} width={width}>
            <CartesianGrid stroke="#E5E1D8" vertical={false} />
            <XAxis dataKey="label" stroke="#8C9B90" tickLine={false} axisLine={false} />
            <YAxis domain={yAxisDomain} stroke="#8C9B90" tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={legendStyle} />
            {fruits.map((fruit) => (
              <Line key={fruit.key} type="monotone" dataKey={fruit.key} name={fruit.label} stroke={lineColors[fruit.key]} strokeWidth={2.4} dot={dotStyle} activeDot={activeDotStyle} />
            ))}
          </LineChart>
        ) : (
          <div className="flex h-full items-center justify-center rounded-xl bg-[#F9F6F0] text-center text-[#4A5D4E]" data-testid="dashboard-empty-chart-message">Add your first reflection to begin seeing trends.</div>
        )}
      </div>
    </section>
  );
};